/**
 * Listen — the crowd clip-validation surface (community-speech plan,
 * Phase A). Common Voice's engine adapted to AfriAnnotate: a
 * full-attention player with Yes / No / Skip / Report, a criteria
 * sheet derived from the project's audio-QC profile (so voters, the
 * recorder hints, and the machine checks enforce the SAME bar), and
 * keyboard shortcuts for fast throughput.
 *
 * Votes are small writes — on desktop/mobile they queue offline like
 * annotations via the existing fetch layers.
 */
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Button, useToast } from "@humansignal/ui";
import { useParams } from "../../providers/RoutesProvider";
import { ProjectContext } from "../../providers/ProjectProvider";
import { ProjectNav } from "../ProjectDashboard/ProjectNav";
import { TabHeader } from "../../components/TabHeader/TabHeader";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { useTranslation } from "../../i18n/LocaleProvider";
import { cn } from "../../utils/bem";
import { apiUrl } from "../../utils/jwt-storage";
import "./ValidatePage.prefix.css";

const block = cn("validate-page");

const getCsrfToken = () => {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
};

const REPORT_REASONS = ["wrong_language", "misread", "offensive", "bad_audio", "other"];

/** Prompt submission panel (Phase E) — shown on prompt-collection
 *  projects. License attestation is REQUIRED; submissions flow into
 *  the same vote loop everyone is already using on this page. */
const PromptSubmitPanel = ({ projectId, maxWords }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [text, setText] = useState("");
  const [source, setSource] = useState("");
  const [license, setLicense] = useState("");
  const [busy, setBusy] = useState(false);
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;

  const submit = async () => {
    if (busy || !text.trim() || !license) return;
    setBusy(true);
    try {
      const r = await fetch(apiUrl(`/api/projects/${projectId}/prompts/`), {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCsrfToken(),
        },
        body: JSON.stringify({ text, source, license }),
      });
      const b = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.show({ message: t("pages.validate.prompt_submitted_toast") });
        setText("");
      } else {
        toast.show({ type: "error", message: b.detail || `(${r.status})` });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={cn("validate-page").elem("submit-panel").toClassName()}>
      <h4>{t("pages.validate.submit_prompt_title")}</h4>
      <textarea
        rows={2}
        value={text}
        placeholder={t("pages.validate.submit_prompt_placeholder")}
        onChange={(e) => setText(e.target.value)}
      />
      <div className={cn("validate-page").elem("submit-row").toClassName()}>
        <input
          type="text"
          value={source}
          placeholder={t("pages.validate.prompt_source_placeholder")}
          onChange={(e) => setSource(e.target.value)}
        />
        <select value={license} onChange={(e) => setLicense(e.target.value)}>
          <option value="">{t("pages.validate.license_choose")}</option>
          <option value="cc0">{t("pages.validate.licenses.cc0")}</option>
          <option value="cc_by">{t("pages.validate.licenses.cc_by")}</option>
          <option value="own_work">{t("pages.validate.licenses.own_work")}</option>
        </select>
        <span
          className={cn("validate-page")
            .elem("wordcount")
            .mod({ over: words > maxWords })
            .toClassName()}
        >
          {words}/{maxWords}
        </span>
        <Button
          type="button"
          size="small"
          waiting={busy}
          disabled={!text.trim() || !license || words > maxWords}
          onClick={submit}
          aria-label={t("pages.validate.submit_prompt")}
        >
          {t("pages.validate.submit_prompt")}
        </Button>
      </div>
    </div>
  );
};

/** Opt-in-only leaderboard (Phase D) — last 30 days, refreshed as the
 *  session progresses. Hidden entirely when nobody has opted in. */
const ListenLeaderboard = ({ projectId, judged }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(apiUrl(`/api/projects/${projectId}/leaderboard/?days=30`), {
          credentials: "same-origin",
          cache: "no-store",
        });
        if (!r.ok) return;
        const b = await r.json();
        if (alive) setRows(b.leaderboard || []);
      } catch {
        /* hidden */
      }
    })();
    return () => {
      alive = false;
    };
    // ``judged`` in deps: refresh standings as this session's votes land.
  }, [projectId, judged]);

  if (!rows || rows.length === 0) return null;
  return (
    <div className={cn("validate-page").elem("leaderboard").toClassName()}>
      <h4>{t("pages.validate.leaderboard_title")}</h4>
      <ol>
        {rows.slice(0, 5).map((r) => (
          <li key={r.rank} className={cn("validate-page").elem("leader").mod({ you: r.you }).toClassName()}>
            <span>
              {r.name}
              {r.you ? ` (${t("pages.validate.you")})` : ""}
            </span>
            <strong>{r.units}</strong>
          </li>
        ))}
      </ol>
    </div>
  );
};

export const ValidatePage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const params = useParams();
  const projectId = params.id;

  const [clip, setClip] = useState(null); // null = loading; {annotation_id:null} = done
  const [busy, setBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  // Autoplay arms only after the first interaction (play/vote/skip):
  // the rapid-fire loop keeps its no-click cadence, but landing on the
  // page no longer blasts audio unprompted (owner-requested).
  const [autoArm, setAutoArm] = useState(false);
  const [judged, setJudged] = useState(0);
  // Reviewer re-check queue (hybrid mode / managers): crowd verdicts a
  // reviewer may overrule. "" = normal voting queue.
  const [queue, setQueue] = useState("");
  const [speed, setSpeed] = useState(1);
  const [error, setError] = useState("");
  const audioRef = useRef(null);

  const loadNext = useCallback(
    async (q = queue) => {
      setClip(null);
      setReportOpen(false);
      setError("");
      try {
        const r = await fetch(apiUrl(`/api/projects/${projectId}/validate/next/${q ? `?queue=${q}` : ""}`), {
          credentials: "same-origin",
          cache: "no-store",
        });
        const b = await r.json().catch(() => ({}));
        if (!r.ok) {
          setError(b.detail || b.error || `(${r.status})`);
          setClip({ annotation_id: null });
          return;
        }
        setClip(b);
      } catch {
        setError(t("pages.validate.load_failed"));
        setClip({ annotation_id: null });
      }
    },
    [projectId, t, queue],
  );

  useEffect(() => {
    if (projectId) loadNext();
  }, [projectId, loadNext]);

  // Reviewer override: the human verdict that beats the vote.
  const override = useCallback(
    async (status) => {
      if (busy || !clip?.annotation_id) return;
      setBusy(true);
      try {
        const r = await fetch(apiUrl(`/api/annotations/${clip.annotation_id}/validate/override/`), {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", "X-CSRFToken": getCsrfToken() },
          body: JSON.stringify({ status }),
        });
        const b = await r.json().catch(() => ({}));
        if (!r.ok) {
          toast.show({ type: "error", message: b.detail || `(${r.status})` });
          return;
        }
        setJudged((n) => n + 1);
        toast.show({ message: status === "validated" ? "Overridden: clip validated." : "Overridden: clip rejected." });
        await loadNext();
      } finally {
        setBusy(false);
      }
    },
    [busy, clip, loadNext, toast],
  );

  const vote = useCallback(
    async (verdict, reason = "") => {
      if (busy || !clip?.annotation_id) return;
      setBusy(true);
      try {
        const r = await fetch(apiUrl(`/api/annotations/${clip.annotation_id}/validate/`), {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCsrfToken(),
          },
          body: JSON.stringify(reason ? { verdict, reason } : { verdict }),
        });
        const b = await r.json().catch(() => ({}));
        if (!r.ok && r.status !== 409) {
          toast.show({ type: "error", message: b.detail || `(${r.status})` });
          return;
        }
        setJudged((n) => n + 1);
        if (b.resolved === "validated") {
          toast.show({ message: t("pages.validate.clip_validated_toast") });
        } else if (b.resolved === "rejected") {
          toast.show({ message: t("pages.validate.clip_rejected_toast") });
        } else if (reason) {
          toast.show({ message: t("pages.validate.reported_toast") });
        }
        await loadNext();
      } finally {
        setBusy(false);
      }
    },
    [busy, clip, loadNext, t, toast],
  );

  const skip = useCallback(() => {
    if (!busy) loadNext();
  }, [busy, loadNext]);

  // Keyboard shortcuts: Y / N / S(kip) / R(eport), Space = replay.
  useEffect(() => {
    const onKey = (e) => {
      if (e.target && /INPUT|TEXTAREA|SELECT/.test(e.target.tagName)) return;
      const k = e.key.toLowerCase();
      if (k === "y") vote("yes");
      else if (k === "n") vote("no");
      else if (k === "s") skip();
      else if (k === "r") setReportOpen((v) => !v);
      else if (k === " ") {
        e.preventDefault();
        const a = audioRef.current;
        if (a) {
          a.currentTime = 0;
          a.play().catch(() => {});
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [vote, skip]);

  const toggleSpeed = () => {
    const next = speed === 1 ? 1.5 : speed === 1.5 ? 0.75 : 1;
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  return (
    <div className={block.toClassName()}>
      {/* Page identity only — the shared project nav mounts in the
          Menubar via ``ValidatePage.context`` below, same pattern as
          Members / Analytics. Rendering ProjectNav here too duplicated
          the pills inside the body and broke the header layout. */}
      <TabHeader title={t("pages.validate.title")} subtitle={t("pages.validate.subtitle")} />

      <div className={block.elem("body").toClassName()}>
        {/* Criteria sheet — derived from the project's QC profile —
            plus the opt-in leaderboard beneath it. */}
        {clip?.criteria && (
          <aside className={block.elem("sidebar").toClassName()}>
            <div className={block.elem("criteria").toClassName()}>
              <h4>{t("pages.validate.criteria_title")}</h4>
              <ul>
                {clip.criteria.map((key) => (
                  <li key={key}>{t(`pages.validate.criteria.${key}`)}</li>
                ))}
              </ul>
            </div>
            <ListenLeaderboard projectId={projectId} judged={judged} />
          </aside>
        )}

        <main className={block.elem("main").toClassName()}>
          {clip === null && (
            <div className={block.elem("empty").toClassName()}>
              <span className={block.elem("spinner").toClassName()} aria-hidden />
            </div>
          )}

          {clip && !clip.annotation_id && (
            <>
              <EmptyState
                title={
                  error ||
                  (queue === "recheck"
                    ? "Nothing to re-check — every crowd verdict has been reviewed."
                    : t("pages.validate.all_done"))
                }
                description={judged > 0 && !error ? `${t("pages.validate.session_count")}: ${judged}` : undefined}
              />
              {/* Reviewers still need a way to reach the other queue
                  when this one is empty. */}
              {(clip.can_override || queue === "recheck") && (
                <div className={block.elem("queue-switch").toClassName()}>
                  <button
                    type="button"
                    className={block
                      .elem("queue-chip")
                      .mod({ active: queue === "" })
                      .toClassName()}
                    onClick={() => setQueue("")}
                  >
                    Vote on new clips
                  </button>
                  <button
                    type="button"
                    className={block
                      .elem("queue-chip")
                      .mod({ active: queue === "recheck" })
                      .toClassName()}
                    onClick={() => setQueue("recheck")}
                  >
                    Re-check crowd verdicts{typeof clip.recheck_count === "number" ? ` (${clip.recheck_count})` : ""}
                  </button>
                </div>
              )}
            </>
          )}

          {clip?.prompts_enabled && <PromptSubmitPanel projectId={projectId} maxWords={clip.prompts_max_words || 20} />}

          {clip?.annotation_id && (
            <>
              {clip.prompt_text && (
                <blockquote className={block.elem("prompt").toClassName()}>{clip.prompt_text}</blockquote>
              )}
              {clip.kind === "prompt" && (
                <div className={block.elem("prompt-meta").toClassName()}>
                  {clip.prompt_license && <span>{t(`pages.validate.licenses.${clip.prompt_license}`)}</span>}
                  {clip.prompt_source && <span>{clip.prompt_source}</span>}
                </div>
              )}
              {clip.kind !== "prompt" && (
                <div className={block.elem("player-row").toClassName()}>
                  {/* Recorded VIDEO clips get a <video> player; recorded
                    audio keeps the <audio> player. Both are
                    HTMLMediaElements, so the shared ref drives Space
                    replay and the speed toggle for either. */}
                  {clip.video_url ? (
                    /* biome-ignore lint/a11y/useMediaCaption: user-supplied media has no caption track */
                    <video
                      ref={audioRef}
                      key={clip.annotation_id}
                      src={clip.video_url}
                      controls
                      autoPlay={autoArm}
                      className={block.elem("player").mod({ video: true }).toClassName()}
                      onPlay={() => setAutoArm(true)}
                      onLoadedMetadata={(e) => {
                        e.currentTarget.playbackRate = speed;
                      }}
                    />
                  ) : (
                    /* biome-ignore lint/a11y/useMediaCaption: user-supplied media has no caption track */
                    <audio
                      ref={audioRef}
                      key={clip.annotation_id}
                      src={clip.audio_url}
                      controls
                      autoPlay={autoArm}
                      className={block.elem("player").toClassName()}
                      onPlay={() => setAutoArm(true)}
                      onLoadedMetadata={(e) => {
                        e.currentTarget.playbackRate = speed;
                      }}
                    />
                  )}
                  <button
                    type="button"
                    className={block.elem("speed").toClassName()}
                    onClick={toggleSpeed}
                    title={t("pages.validate.playback_speed")}
                  >
                    {speed}×
                  </button>
                </div>
              )}

              {clip.resolution && (
                <div className={block.elem("verdict").toClassName()}>
                  Crowd verdict: <b>{clip.resolution.status}</b> ({clip.resolution.yes_votes} yes /{" "}
                  {clip.resolution.no_votes} no)
                  {clip.resolution.overridden && <span> — already overridden</span>}
                </div>
              )}

              {clip.can_override && (
                <div className={block.elem("override").toClassName()}>
                  <span className={block.elem("override-label").toClassName()}>
                    {queue === "recheck" ? "Your verdict overrules the crowd" : "Reviewer override"}
                  </span>
                  <button
                    type="button"
                    className={block.elem("btn").mod({ tone: "yes", small: true }).toClassName()}
                    disabled={busy}
                    onClick={() => override("validated")}
                  >
                    Validate
                  </button>
                  <button
                    type="button"
                    className={block.elem("btn").mod({ tone: "no", small: true }).toClassName()}
                    disabled={busy}
                    onClick={() => override("rejected")}
                  >
                    Reject
                  </button>
                </div>
              )}

              {queue !== "recheck" && (
                <div className={block.elem("actions").toClassName()}>
                  <button
                    type="button"
                    className={block.elem("btn").mod({ tone: "yes" }).toClassName()}
                    disabled={busy}
                    onClick={() => vote("yes")}
                  >
                    {t("pages.validate.yes")} <kbd>Y</kbd>
                  </button>
                  <button
                    type="button"
                    className={block.elem("btn").mod({ tone: "no" }).toClassName()}
                    disabled={busy}
                    onClick={() => vote("no")}
                  >
                    {t("pages.validate.no")} <kbd>N</kbd>
                  </button>
                  <button type="button" className={block.elem("btn").toClassName()} disabled={busy} onClick={skip}>
                    {t("pages.validate.skip")} <kbd>S</kbd>
                  </button>
                  <button
                    type="button"
                    className={block.elem("btn").mod({ tone: "report" }).toClassName()}
                    disabled={busy}
                    onClick={() => setReportOpen((v) => !v)}
                  >
                    {t("pages.validate.report")} <kbd>R</kbd>
                  </button>
                </div>
              )}

              {reportOpen && (
                <div className={block.elem("report").toClassName()}>
                  {REPORT_REASONS.map((reason) => (
                    <button
                      key={reason}
                      type="button"
                      className={block.elem("report-reason").toClassName()}
                      disabled={busy}
                      onClick={() => vote("no", reason)}
                    >
                      {t(`pages.validate.reasons.${reason}`)}
                    </button>
                  ))}
                </div>
              )}

              {(clip.can_override || queue === "recheck") && (
                <div className={block.elem("queue-switch").toClassName()}>
                  <button
                    type="button"
                    className={block
                      .elem("queue-chip")
                      .mod({ active: queue === "" })
                      .toClassName()}
                    onClick={() => setQueue("")}
                  >
                    Vote on new clips
                  </button>
                  <button
                    type="button"
                    className={block
                      .elem("queue-chip")
                      .mod({ active: queue === "recheck" })
                      .toClassName()}
                    onClick={() => setQueue("recheck")}
                  >
                    Re-check crowd verdicts{typeof clip.recheck_count === "number" ? ` (${clip.recheck_count})` : ""}
                  </button>
                </div>
              )}

              {typeof clip.remaining_estimate === "number" && (
                <div className={block.elem("remaining").toClassName()}>
                  {t("pages.validate.remaining")}: {clip.remaining_estimate}
                  {judged > 0 && (
                    <>
                      , {t("pages.validate.session_count")}: {judged}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

ValidatePage.title = "Listen";
ValidatePage.path = "/validate";
ValidatePage.displayName = "ValidatePage";

// Mounts the shared project top-nav while Listen is open — the same
// pattern Members / Analytics use. Without this the page had to render
// ProjectNav in its own body, which duplicated the pills and broke the
// header layout.
ValidatePage.context = () => {
  const { project } = useContext(ProjectContext);
  const { user } = useAuth();
  if (!project?.id) return null;
  const role = user?.active_organization_role;
  const canManage =
    !!user &&
    (user.is_staff || user.is_superuser || ["OW", "AD", "MA"].includes(role) || project?.created_by?.id === user.id);
  return <ProjectNav projectId={project.id} canManage={canManage} project={project} userRole={role} />;
};
